/** アバター基準高さ（scaleH = state.size.y / AVATAR_BASE_HEIGHT）。 */
export const AVATAR_BASE_HEIGHT = 5
/** placeholder 箱の幅・奥行倍率（高さは AVATAR_BASE_HEIGHT * scaleH に追従）。 */
export const AVATAR_PLACEHOLDER_WIDTH = 2
export const AVATAR_PLACEHOLDER_DEPTH = 1
/**
 * 1 歩行サイクル（sin の 2π）で進む水平距離を、アバター身長(=AVATAR_BASE_HEIGHT*scaleH)
 * に対する比率で指定する。距離駆動の位相進行にすることで、進行速度と脚の振りが
 * 常に同期し「足が地面を滑る」現象を防ぐ。値が小さいほど歩幅が短く脚が速く回る。
 */
export const STRIDE_CYCLE_DISTANCE_RATIO = 2.45
/** 上下バウンスの振幅倍率（scaleH に対する相対）。Roblox 標準ランの激しめでリズミカルな上下動に合わせる。 */
export const STRIDE_BOB_AMPLITUDE = 0.17
/** 移動と判定する最小水平速度（stud/秒）。 */
export const STRIDE_MOVE_THRESHOLD = 0.5
/** 移動開始/停止時の振幅エンベロープ係数（毎フレーム指数 lerp で目標値へ収束）。 */
export const STRIDE_AMP_RAMP_IN = 0.2
export const STRIDE_AMP_RAMP_OUT = 0.15
/**
 * 走り時の上腕ピッチ振幅（ラジアン、約 40°）。'groundAmp' で立位は減衰するので、
 * 停止時に大振りはしない。
 */
export const STRIDE_ARM_PITCH_AMP = 0.7
/**
 * 走り時に前方へ振り出す上腕を体の前（内側）へ寄せるクロス量（ラジアン、約 25°）。
 * 前後だけの単調な振りを避け、前に振る腕が斜め前に出る Roblox 標準ランの腕振りにする。
 * 上腕 rotation.z の安静角（REST_POSE_UPPER_ARM_Z）に、前方スイング量に比例して加える。
 */
export const STRIDE_ARM_CROSS = 0.44
/**
 * 走り時の下腕（肘）の基準曲げ角（ラジアン、約 78°）。
 *
 * VRM 1.0 正規化骨の lower arm では、骨の Y 軸（前腕の長さ方向）に対し
 * rotation.x 正方向が「前腕を体の前へ折り畳む（手が肩へ近づく）」自然な
 * 肘曲げに対応する。値を小さくすると視覚的に「ほぼ伸びている」ように見えるので、
 * 走者の腕振りで使われる 90° 近辺まで強めに曲げる。
 * 'groundAmp' で 0(直立)→この値(走り)に lerp し、走り出しに肘が立ち、
 * 停止で自然に伸びる。
 */
export const STRIDE_LOWER_ARM_BEND = 1.36
/** 走り時の上脚ピッチ振幅（ラジアン、約 23°）。歩幅と接地（cycleDistance）を同期させ足滑りを防ぐ。 */
export const STRIDE_LEG_PITCH_AMP = 0.41
/**
 * 走り時の足首ロール振幅（ラジアン、約 19°）。前方接地でかかとから入る（つま先上げ=
 * dorsiflex）、後方でつま先で蹴る（plantarflex）足運びを表現する。脚の前後位相に同期。
 */
export const STRIDE_FOOT_ROLL = 0.34

/**
 * 死亡（体力 0）の倒れ込みポーズの追従係数。1 フレームあたりこの割合で目標へ
 * 近づく指数 lerp。60fps で約 0.2 秒かけて倒れ、唐突な瞬間転倒を避ける。
 */
export const DEATH_POSE_LERP = 0.18

/** ダッシュ量（0..1）の追従係数。motionState.isDashing の on/off を平滑化する。 */
export const DASH_AMP_LERP = 0.12
/** ダッシュ時の腕脚スイング/バウンス増幅率（0.75 = 通常の +75% まで増やす）。 */
export const DASH_STRIDE_AMP_BOOST = 0.75
/** ダッシュ時の前傾角（ラジアン、約 11°）。Roblox 標準ランの軽い前傾に合わせる（前傾しすぎない）。 */
export const DASH_LEAN_RAD = 0.2
/**
 * ダッシュ時の腿の追加持ち上げ量（ラジアン、約 52°）。base のスイングに上乗せして
 * 膝を高く上げる。上脚 rotation.x 正で前方へ持ち上がる。Roblox 標準ランの
 * 膝を高く上げてリズミカルに走るフォームに合わせる。
 */
export const DASH_THIGH_LIFT = 0.9
/**
 * ダッシュ時の膝（下脚）曲げ量（ラジアン、約 92°）。前方へ振り出す脚ほど深く畳み
 * （足を尻へ引き寄せる）、Roblox 標準ランの膝を深く曲げてリズミカルに走るフォームにする。
 * 下脚 rotation.x 負で膝が曲がる。
 */
export const DASH_KNEE_BEND = 1.6
/** ダッシュ走行中、後方の脚にも残す膝曲げの最低割合（0..1）。膝が伸び切らない athletic な構え。 */
export const DASH_KNEE_BASE_FRAC = 0.22

// --- 遊泳ポーズ（terrain.water の簡易遊泳中） ------------------------------
// 移動中（WASD）= 平泳ぎ（うつ伏せ水平）、停止中 = 浮遊（直立で頭だけ
// 水面上に出す立ち泳ぎ）。両者は移動エンベロープでクロスフェードする
// （直立 0 → 前傾 1.25 rad は同符号方向の補間なので中間で打ち消されない）。
//
// 平泳ぎは「キャッチ → 呼吸 → リカバリー → キック → グライド」の周期運動
// （左右対称・両腕両脚は同時。交互運動にしない）を正規化キーフレームで駆動:
//   0.00-0.10 グライド（流線形に伸びる）
//   0.10-0.25 キャッチ〜アウトスイープ（両手を外へ開いて水をつかむ）
//   0.25-0.38 インスイープ + 呼吸（肘を曲げ手を胸前へ。胸が上がり口が水面上へ）
//   0.38-0.50 リカバリー（手を前方へ戻し、頭を水中へ）
//   0.45-0.60 脚の引き付け（かかとをお尻へ。腕と同時に大きく動かさない）
//   0.60-0.78 ウィップキック（足裏で水を後方へ。ここで最も加速）
//   0.78-1.00 グライド保持（細長く伸びて滑る）
/** 遊泳ポーズ量（0..1）の追従係数。motionState.isSwimming の on/off を平滑化する。 */
export const SWIM_POSE_LERP = 0.1
/** 滑走ポーズ量（0..1）の追従係数。motionState.isSliding の on/off を平滑化する。 */
export const SLIDE_POSE_LERP = 0.18
/**
 * すべり台滑走の後傾角（ラジアン）。仰向けに倒れて足から滑り降りるポーズ。
 * モデルの rotation.x 正は後傾（頭が +Z 側）。滑走の向き（rotationY）は
 * 物理側が下り方向へ向けるため、後傾すると頭が坂の上側になる。
 */
export const SLIDE_LEAN_RAD = 1.25

// --- 着席ポーズ（vehicle 乗車中） ------------------------------------------
/** 着席ポーズ量（0..1）の追従係数。motionState.isSeated の on/off を平滑化する。 */
export const SIT_POSE_LERP = 0.18
/**
 * 着席時の股関節屈曲（ラジアン、約 80°）。上脚 rotation.x 正で腿が前方へ
 * 持ち上がり、座面に腿を載せた姿勢になる。
 */
export const SIT_HIP_FLEX_RAD = 1.4
/** 着席時の膝曲げ（ラジアン、約 80°）。下脚 rotation.x 負で膝が曲がる。 */
export const SIT_KNEE_BEND_RAD = -1.4
/**
 * 着席時の上腕ピッチ（ラジアン）。正で肘が前・負で肘が後ろへ動く。0 で肘が
 * 胸の前あたりに来て、前腕の肘曲げ（SIT_ELBOW_BEND_RAD）と合わせて手が
 * ハンドルへ乗る（実機ビューで確定）。
 */
export const SIT_UPPER_ARM_PITCH_RAD = 0
/** 着席時の肘曲げ（ラジアン、約 92°）。前腕を前下方へ伸ばし、手をハンドルへ乗せる（下腕 rotation.y へ正の曲げ量として乗せる）。 */
export const SIT_ELBOW_BEND_RAD = 1.6

/**
 * 浮遊（停止中）の前傾角。直立（0）のまま水に浮かび、頭だけが水面から出る
 * （物理のカプセル中心は水面下 1.2 studs なので、直立なら頭頂が水面上に出る）。
 */
export const SWIM_FLOAT_LEAN_RAD = 0
/** 平泳ぎ（移動中）の基本前傾角（ラジアン、約 72°）。うつ伏せの水平姿勢。 */
export const SWIM_BREAST_LEAN_RAD = 1.25
/** 平泳ぎ 1 ストローク（サイクル）の長さ（秒）。仕様: 約 1.4〜1.8 秒。 */
export const BREAST_CYCLE_SECONDS = 1.5
/** 浮遊時に腕を前方へわずかに掲げる角（ラジアン）。脱力した浮き身。 */
export const SWIM_FLOAT_ARM_RAD = 0.25
/** 浮遊時に腕を体側から外へ開く角（ラジアン）。 */
export const SWIM_FLOAT_ARM_SPREAD_RAD = 0.2
/** 浮遊時の上下の揺れ（studs）。平泳ぎ中は呼吸の上下動に置き換わる。 */
export const SWIM_FLOAT_BOB_AMPLITUDE = 0.12
/**
 * 呼吸（胸上げ）での前傾の戻し角（ラジアン、約 14°）。体幹はほんの少しだけ
 * 起こす — 体ごと大きく傾けると不自然になるため、顔出しは首・頭の曲げが主役。
 */
export const BREAST_BREATH_PITCH_RAD = 0.25
/** 呼吸での身体の浮き上がり（studs）。口が水面上に出る程度。 */
export const BREAST_BREATH_LIFT = 0.35
/**
 * 呼吸時に首を曲げて前を向く角（ラジアン）。体幹の小さな胸上げと合わせて、
 * 顔が水面から出て前方を向く。首と頭へ分配して 1 関節で反らせ過ぎない。
 */
export const BREAST_BREATH_NECK_RAD = 0.45
export const BREAST_BREATH_HEAD_RAD = 0.55
/** キック後の前方サージの最大量（studs、見た目のみ）。控えめにして揺れを抑える。 */
export const BREAST_SURGE_STUDS = 0.35

/** 正規化キーフレーム列（[位相 0..1, 値] の昇順。端の値を同じにしてループ）。 */
export type SwimKeyframes = ReadonlyArray<readonly [number, number]>
// 平泳ぎ各チャンネルのキーフレーム。値はラジアン（呼吸・サージのみ 0..1 係数）。
// 「腕は腰まで引かない」「脚はバタ足にしない」「腕と脚を同時に大きく動かさない」
// 「キック後に必ずグライドを入れる」という平泳ぎの原則をタイムラインで表現する。
/**
 * 上腕の前方リーチ（rotation.x）。0 = 体側（脚側へ垂らした腕）、約 π で
 * 頭の先（体軸の延長）へ伸びる。グライドでは頭の先へまっすぐ伸ばし、
 * かきで大きな円弧を描いて胸の方へ引き、リカバリーで再び頭の先へ戻す。
 */
export const BREAST_ARM_PITCH_KEYS: SwimKeyframes = [
  [0.0, 2.95],
  [0.1, 2.9],
  [0.25, 2.5],
  [0.32, 1.5],
  [0.42, 2.05],
  [0.5, 2.95],
  [1.0, 2.95],
]
/**
 * 上腕の外開き（rotation.z の外側デルタ）。アウトスイープで腕を伸ばしたまま
 * Y 字（肩幅より少し広い）まで開き、インスイープで胸の下へ閉じる。
 */
export const BREAST_ARM_SPREAD_KEYS: SwimKeyframes = [
  [0.0, 0.0],
  [0.1, 0.15],
  [0.25, 0.6],
  [0.32, 0.12],
  [0.42, 0.05],
  [0.5, 0.0],
  [1.0, 0.0],
]
/**
 * 肘の曲げ。アウトスイープ中はほぼ伸ばしたまま（実技どおり）、キャッチ〜
 * インスイープで深く（約 75°）曲げて手を胸の下へ集め、リカバリーで前方へ
 * 伸ばし切る。
 */
export const BREAST_ELBOW_KEYS: SwimKeyframes = [
  [0.0, 0.05],
  [0.1, 0.1],
  [0.25, 0.1],
  [0.3, 1.3],
  [0.38, 1.1],
  [0.5, 0.05],
  [1.0, 0.05],
]
/** 呼吸（胸上げ 0..1）。インスイープで最大、リカバリーで水中へ戻す。 */
export const BREAST_BREATH_KEYS: SwimKeyframes = [
  [0.0, 0.0],
  [0.15, 0.0],
  [0.3, 1.0],
  [0.38, 1.0],
  [0.5, 0.0],
  [1.0, 0.0],
]
/** 股関節の引き付け（上脚 rotation.x）。腕が前へ戻ってから曲げ、キックで伸ばす。 */
export const BREAST_HIP_KEYS: SwimKeyframes = [
  [0.0, 0.0],
  [0.45, 0.0],
  [0.6, 0.5],
  [0.74, -0.06],
  [0.8, 0.0],
  [1.0, 0.0],
]
/** 膝の曲げ（下脚 rotation.x、負で曲がる）。かかとをお尻へ → 蹴り伸ばして閉じる。 */
export const BREAST_KNEE_KEYS: SwimKeyframes = [
  [0.0, 0.0],
  [0.45, 0.0],
  [0.6, -1.7],
  [0.76, 0.0],
  [1.0, 0.0],
]
/**
 * 脚の外開き（上脚 rotation.z の外側デルタ）。引き付けで足先を外へ向けて開き、
 * キックで外後方へ回し込み、最後に内側へ挟んで閉じる（ウィップキック）。
 */
export const BREAST_LEG_SPREAD_KEYS: SwimKeyframes = [
  [0.0, 0.0],
  [0.45, 0.0],
  [0.6, 0.4],
  [0.7, 0.55],
  [0.78, 0.0],
  [1.0, 0.0],
]
/** 前方サージ（0..1）。キック（0.60-0.78）で加速し、グライドで滑って減速。 */
export const BREAST_SURGE_KEYS: SwimKeyframes = [
  [0.0, 0.5],
  [0.25, 0.0],
  [0.55, 0.0],
  [0.72, 1.0],
  [0.85, 0.8],
  [1.0, 0.5],
]

/**
 * キーフレーム表のループ前提（0 始まり・1 終わり・端点値一致・2 点以上）を
 * モジュール初期化時に検証する。端点がズレるとサイクル境界でポーズが
 * ポップするため、定数の編集ミスを早期に検出する。
 */
const assertLoopingSwimKeyframes = (
  name: string,
  keys: SwimKeyframes,
): SwimKeyframes => {
  if (keys.length < 2) {
    throw new Error(`${name}: キーフレームは 2 点以上必要です`)
  }
  if (keys[0][0] !== 0 || keys[keys.length - 1][0] !== 1) {
    throw new Error(
      `${name}: キーフレームは位相 0 で始まり 1 で終わる必要があります`,
    )
  }
  if (keys[0][1] !== keys[keys.length - 1][1]) {
    throw new Error(`${name}: ループ端点（位相 0 と 1）の値が一致していません`)
  }
  return keys
}
for (const [name, keys] of [
  ['BREAST_ARM_PITCH_KEYS', BREAST_ARM_PITCH_KEYS],
  ['BREAST_ARM_SPREAD_KEYS', BREAST_ARM_SPREAD_KEYS],
  ['BREAST_ELBOW_KEYS', BREAST_ELBOW_KEYS],
  ['BREAST_BREATH_KEYS', BREAST_BREATH_KEYS],
  ['BREAST_HIP_KEYS', BREAST_HIP_KEYS],
  ['BREAST_KNEE_KEYS', BREAST_KNEE_KEYS],
  ['BREAST_LEG_SPREAD_KEYS', BREAST_LEG_SPREAD_KEYS],
  ['BREAST_SURGE_KEYS', BREAST_SURGE_KEYS],
] as const) {
  assertLoopingSwimKeyframes(name, keys)
}

/** キーフレーム列を位相 p（0..1）で補間する（区間ごとに smoothstep）。 */
export const sampleSwimKeyframes = (keys: SwimKeyframes, p: number): number => {
  for (let i = 0; i < keys.length - 1; i += 1) {
    const [t0, v0] = keys[i]
    const [t1, v1] = keys[i + 1]
    if (p >= t0 && p <= t1) {
      const u = t1 === t0 ? 1 : (p - t0) / (t1 - t0)
      const smoothed = u * u * (3 - 2 * u)
      return v0 + (v1 - v0) * smoothed
    }
  }
  return keys[keys.length - 1][1]
}
/**
 * 遊泳中にモデルを見た目だけ持ち上げる量（studs）。物理のカプセル中心は
 * 水面下（SWIM_SURFACE_CENTER_DEPTH）にあり、水平姿勢のままだとほぼ全身が
 * 水没して見えるため、実機のように身体の上半分が水面から出る位置へ補正する。
 * うつ伏せの平泳ぎ（移動中）は背中が出るようさらに持ち上げる。
 */
// 浮遊（直立）は顔が完全に水面から出る高さまで持ち上げる。
export const SWIM_SURFACE_VISUAL_LIFT = 0.7
// 平泳ぎ（うつ伏せ水平）は背中が水面に出るよう移動中はさらに持ち上げる。
export const SWIM_SURFACE_VISUAL_LIFT_MOVE_EXTRA = 0.3

/**
 * ジャンプ判定用の垂直速度しきい値（stud/秒）。位置の Y 差分から推定するため、
 * 微小な物理ノイズで airborne 誤判定にならないよう余裕を持たせる。
 */
export const JUMP_VERTICAL_VELOCITY_THRESHOLD = 0.5
/**
 * 走行中の下り坂・段差で grounded がフレームごとにチラついても、走行モーションが
 * 両脚揃えの空中ポーズへ飛ばないよう、上昇でない短時間の非接地を接地として吸収する
 * 猶予（秒）。接地フレームでリチャージし、これを超えて続く非接地は本物の落下/滞空と
 * みなして空中へ移行する。坂面の階段状落差による grounded チラつきの周期を吸収できる
 * 程度に短く、本物の落下の検出遅れが目立たない長さにする。
 */
export const RUN_SLOPE_AIRBORNE_IGNORE_DURATION = 0.15
/** 上記の非接地吸収を適用する走行中判定のストライド下限（0..1）。 */
export const RUN_SLOPE_AIRBORNE_MIN_STRIDE = 0.5
/** ジャンプ姿勢の指数 lerp 係数（離陸/着地時のブレンド速度）。 */
export const JUMP_POSE_LERP = 0.3
/**
 * ジャンプ動作のポーズ目標値（ラジアン）。立位・走行を問わず、本家 Roblox の
 * デフォルトジャンプに合わせた左右対称の「両手を上げる」フォームへ統一する。
 * すべて加算合成で 0(基本姿勢) → 各目標へ重み付き lerp される。
 * 軸の向きは VRM 1.0 正規化骨に従う：
 *   - 上腕 rotation.x 正 = 後方〜上方スイング（約 π で頭上）、負 = 前方/下方
 *   - 上脚 rotation.x 正 = 膝を前へ持ち上げる
 *   - 下脚 rotation.x 負 = 膝を曲げてふくらはぎを後方へ
 */

/**
 * 空中フォーム：両腕を頭上へ振り上げる（左右対称、本家ジャンプ準拠）。
 * 空中（airborne）の間ずっと保持し、上昇・頂点・下降で腕が落ちないようにする。
 */
export const POSE_JUMP_UPPER_ARM = 2.5 // 上腕を頭上方向へ振り上げる（約 +143°）
export const POSE_JUMP_LOWER_ARM = 0.2 // 肘はほぼ伸ばす（わずかに曲げる）

/** 屈伸（予備動作）の脚目標。現状 prepAmp=0 で無効だが脚ポーズ式が参照する。 */
export const POSE_PREP_UPPER_LEG = 1.0
export const POSE_PREP_LOWER_LEG = -1.5

/** テイクオフ — 全身を伸ばして地面を蹴る（脚。腕は JUMP で上げる）。 */
export const POSE_TAKEOFF_UPPER_LEG = -0.1 // 完全に伸ばす（やや反らせる）
export const POSE_TAKEOFF_LOWER_LEG = 0.0

/** 上昇 — 脚をやや軽く折る（自然落下姿勢）。 */
export const POSE_ASCENT_UPPER_LEG = 0.3
export const POSE_ASCENT_LOWER_LEG = -0.6

/** 下降 — 足を地面へ向ける。 */
export const POSE_DESCENT_UPPER_LEG = -0.1
export const POSE_DESCENT_LOWER_LEG = -0.05

/** 着地（衝撃吸収）— 腕は脇へ下ろし、膝を前へ突き出して深く折る。 */
export const POSE_LANDING_UPPER_ARM = -0.3
export const POSE_LANDING_LOWER_ARM = 0.5 // 肘軽く曲げる（バランス取り）
export const POSE_LANDING_UPPER_LEG = 0.9
export const POSE_LANDING_LOWER_LEG = -1.5

/** テイクオフ衝撃の持続時間（秒）。短い瞬間的ピーク。 */
export const TAKEOFF_IMPULSE_DURATION = 0.12
/**
 * 上昇/下降の振り分けに使う基準速度（stud/秒）。|vy|/REF を 0..1 にクランプ。
 * Roblox 準拠の重力(196.2)・ジャンプ初速(50)に合わせ、跳躍の弧全体でポーズが
 * 滑らかに上昇→下降へ遷移するよう、初速の約半分(25)を基準にする。
 */
export const VERTICAL_VELOCITY_REFERENCE = 25
/** 着地時の屈伸ダウン時間（秒）。 */
export const LANDING_CROUCH_DURATION = 0.18
/**
 * 屈伸・着地時の最大沈み込み（scaleH に対する相対）。
 *
 * 深いスクワット姿勢では「太もも +1.0 rad / 脛 -1.5 rad」で足首が骨盤から
 * 約 0.7×LEG_LENGTH 下に来る幾何で、立位より約 0.3×LEG_LENGTH 浮く。
 * その浮きを補償するため modelRoot を下げる必要があり、scaleH 相対で 0.6
 * 程度（脚長の約 60%）が「足はほぼ地面、上体の重心は明確に下がる」値。
 */
export const LANDING_CROUCH_DIP = 0.6
